import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { DateTime } from 'luxon';
import { UpdatedSetupSummary } from '../../../../models/setup/setup.models';
import { DailyLogHeaderSnapshot, DailyLogSummaryHeaderStateService } from './daily-log-summary-header-state.service';
import { ValidationResult } from '../../../../models/projects/validation.models';

// Presentation shared by the Daily Log and the section-page date picker.
// Each parent retains its existing loading, date and navigation logic.
@Component({
  selector: 'app-daily-log-summary-header',
  templateUrl: 'daily-log-summary-header.html'
})
export class DailyLogSummaryHeaderComponent implements OnChanges {
  @Input() dailyLogDisplayDate: string;
  @Input() currentLogDate: DateTime;
  @Input() maxDate: DateTime;
  @Input() activeLogDate: DateTime;
  @Input() setupSummary: UpdatedSetupSummary;
  @Input() validationResult: ValidationResult;
  @Input() dateLoading: boolean;
  @Input() dateChangeLoading: boolean;
  @Input() summaryLoading: boolean;
  @Input() navigationLoading = false;
  @Input() disabled: boolean;
  @Input() canGoForward: boolean;
  @Input() isModal: boolean = false;

  @Output() previousDay = new EventEmitter<void>();
  @Output() nextDay = new EventEmitter<void>();
  @Output() activeDay = new EventEmitter<void>();
  @Output() currentLogDateChange = new EventEmitter<DateTime>();
  @Output() dateChange = new EventEmitter<DateTime>();
  @Output() datePickerHover = new EventEmitter<void>();

  private cachedHeader: DailyLogHeaderSnapshot;
  private retainedActiveDayAction: boolean;

  constructor(private headerState: DailyLogSummaryHeaderStateService) {
    this.retainedActiveDayAction = this.headerState.getActiveDayAction();
  }

  get displayDate(): string {
    return this.dailyLogDisplayDate || (this.cachedHeader ? this.cachedHeader.displayDate : null);
  }

  get displaySummary(): UpdatedSetupSummary {
    return this.setupSummary || (this.cachedHeader ? this.cachedHeader.summary : null);
  }

  get showActiveDayAction(): boolean {
    if (this.activeDayActionPending) {
      return !this.isModal && !!(this.retainedActiveDayAction ?? this.headerState?.getActiveDayAction());
    }
    if (!this.activeLogDate?.isValid || !this.currentLogDate?.isValid
      || this.activeLogDate.toMillis() === this.currentLogDate.toMillis()) { return false; }
    // The forward arrow adds a calendar day, then applies that day's vessel offset.
    // Compare local dates so 23/25-hour reporting days are treated as adjacent too.
    return this.currentLogDate.plus({ days: 1 }).toISODate() !== this.activeLogDate.toISODate();
  }

  get activeDayActionPending(): boolean {
    return this.navigationLoading || this.dateLoading || this.dateChangeLoading || this.summaryLoading
      || !this.activeLogDate?.isValid || !this.currentLogDate?.isValid;
  }

  ngOnChanges() {
    this.cachedHeader = this.isModal ? null : this.headerState.getForCurrentDay();
    if (!this.isModal && !this.activeDayActionPending) {
      this.retainedActiveDayAction = this.showActiveDayAction;
      this.headerState.rememberActiveDayAction(this.retainedActiveDayAction);
      if (this.validationResult) {
        this.headerState.rememberValidation(this.currentLogDate, this.validationResult);
      }
    }
    if (!this.isModal && !this.dateLoading && !this.dateChangeLoading && !this.summaryLoading &&
        this.currentLogDate?.isValid && this.activeLogDate?.isValid && this.dailyLogDisplayDate && this.setupSummary) {
      this.headerState.remember(this.currentLogDate, this.dailyLogDisplayDate, this.setupSummary);
    }
  }

  onDateChange(value: DateTime) {
    this.currentLogDate = value;
    this.currentLogDateChange.emit(value);
    this.dateChange.emit(value);
  }
}



// WEBPACK FOOTER //
// ./src/pages/shared/components/daily-log-summary-header/daily-log-summary-header.ts